import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";

import { Grid, makeStyles, Paper, Typography } from "@material-ui/core";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";

import CoinField from "../CoinSwapper/CoinField";
import CoinDialog from "../CoinSwapper/CoinDialog";
import LoadingButton from "../Components/LoadingButton";
import WrongNetwork from "../Components/wrongNetwork";

import {
  getAmountOut,
  getBalanceAndSymbol,
  getReserves,
} from "../ethereumFunctions";
import { addLiquidity, quoteAddLiquidity } from "./LiquidityFunctions";

import useFormat from "../hooks/useFormat";
import useChangeQuery from "../hooks/useChangeQuery";
import { searchCoin } from "../apiFunctions";

const configs = require("../configs.json");

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    width: "100%",
    overflow: "wrap",
    background: `linear-gradient(${configs.theme.linearGradient}) !important`,
    color: "white",
  },
  fullWidth: {
    width: "100%",
  },
  values: {
    width: "50%",
  },
  title: {
    textAlign: "center",
    padding: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  hr: {
    width: "100%",
    color: theme.palette.grey[400],
  },
  balance: {
    padding: theme.spacing(1),
    overflow: "wrap",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.4),
  },
}));

function LiquidityDeployer({ network }) {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const [formatBalance, formatReserve] = useFormat();
  const [changeQueryParam] = useChangeQuery();

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // Stores a record of whether their respective dialog window is open
  const [dialog1Open, setDialog1Open] = useState(false);
  const [dialog2Open, setDialog2Open] = useState(false);
  const [wrongNetworkOpen, setwrongNetworkOpen] = useState(false);

  // Stores data about their respective coin
  const [coin1, setCoin1] = useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
    decimal: undefined,
  });
  const [coin2, setCoin2] = useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
    decimal: undefined,
  });

  // Stores the current reserves in the liquidity pool between coin1 and coin2
  const [reserves, setReserves] = useState(["0.0", "0.0"]);

  // Stores the current value of their respective text box
  const [field1Value, setField1Value] = useState("");
  const [field2Value, setField2Value] = useState("");
  const [amountsRate, setAmountsRate] = useState([null, null]);

  // Controls the loading button
  const [loading, setLoading] = useState(false);

  // Stores the user's balance of liquidity tokens for the current pair
  const [liquidityTokens, setLiquidityTokens] = useState("");

  // Used when getting a quote of liquidity
  const [liquidityOut, setLiquidityOut] = useState([0, 0, 0]);

  // Switches the top and bottom coins, this is called when users hit the swap button or select the opposite
  // token in the dialog (e.g. if coin1 is TokenA and the user selects TokenB when choosing coin2)
  const switchFields = () => {
    let oldField1Value = field1Value;
    let oldField2Value = field2Value;

    setCoin1(coin2);
    setCoin2(coin1);
    setField1Value(oldField2Value);
    setField2Value(oldField1Value);
    setReserves(reserves.reverse());

    changeQueryParam("liquidity", coin2.symbol, coin1.symbol);
  };

  // These functions take an HTML event, pull the data out and puts it into a state variable.
  const handleChange = {
    field1: (e) => {
      setField1Value(e.target.value);

      if (amountsRate[0] && amountsRate[1]) {
        let field2 = parseFloat(
          (amountsRate[1] * e.target.value) / amountsRate[0]
        ).toPrecision(8);

        setField2Value(field2);

        quoteAddLiquidity(
          coin1.address,
          coin2.address,
          e.target.value,
          field2,
          network.factory
        ).then((data) => {
          console.log("TokenA in: ", data[0]);
          console.log("TokenB in: ", data[1]);
          console.log("Liquidity out: ", data[2]);
          setLiquidityOut([data[0], data[1], data[2]]);
        });
      } else if (+field2Value) {
        quoteAddLiquidity(
          coin1.address,
          coin2.address,
          e.target.value,
          field2Value,
          network.factory
        ).then((data) => {
          console.log("TokenA in: ", data[0]);
          console.log("TokenB in: ", data[1]);
          console.log("Liquidity out: ", data[2]);
          setLiquidityOut([data[0], data[1], data[2]]);
        });
      }
    },
    field2: (e) => {
      setField2Value(e.target.value);

      if (amountsRate[0] && amountsRate[1]) {
        let field1 = parseFloat(
          (amountsRate[0] * e.target.value) / amountsRate[1]
        ).toPrecision(8);

        setField1Value(field1);

        quoteAddLiquidity(
          coin1.address,
          coin2.address,
          field1,
          e.target.value,
          network.factory
        ).then((data) => {
          console.log("TokenA in: ", data[0]);
          console.log("TokenB in: ", data[1]);
          console.log("Liquidity out: ", data[2]);
          setLiquidityOut([data[0], data[1], data[2]]);
        });
      } else if (+field1Value) {
        quoteAddLiquidity(
          coin1.address,
          coin2.address,
          field1Value,
          e.target.value,
          network.factory
        ).then((data) => {
          console.log("TokenA in: ", data[0]);
          console.log("TokenB in: ", data[1]);
          console.log("Liquidity out: ", data[2]);
          setLiquidityOut([data[0], data[1], data[2]]);
        });
      }
    },
  };

  // Determines whether the button should be enabled or not
  const isButtonEnabled = () => {
    // If both coins have been selected, and a valid float has been entered for both, which are less than the user's balances, then return true
    const parsedInput1 = parseFloat(field1Value);
    const parsedInput2 = parseFloat(field2Value);

    return (
      coin1.address &&
      coin2.address &&
      parsedInput1 !== NaN &&
      0 < parsedInput1 &&
      parsedInput2 !== NaN &&
      0 < parsedInput2 &&
      parsedInput1 <= coin1.balance &&
      parsedInput2 <= coin2.balance
    );
  };

  const deploy = () => {
    console.log("Attempting to deploy liquidity...");
    setLoading(true);

    addLiquidity(
      coin1.address,
      coin2.address,
      field1Value,
      field2Value,
      "0",
      "0",
      network.router,
      network.account,
      network.wethAddress,
      (res) => {
        if (res.status) {
          setLoading(false);

          // If the transaction was successful, we clear to input to make sure the user doesn't accidental redo the transfer
          setField1Value("");
          setField2Value("");
          enqueueSnackbar("Deployment Successful", {
            variant: "success",
            autoHideDuration: 5000,
          });
        } else {
          setLoading(false);
          enqueueSnackbar("Deployment Failed (" + res.e.reason + ")", {
            variant: "error",
            autoHideDuration: 5000,
          });
        }
      }
    ).catch((e) => {
      console.log(e);
      setLoading(false);
      enqueueSnackbar("Deployment Failed (" + e.reason + ")", {
        variant: "error",
        autoHideDuration: 5000,
      });
    });
  };

  // Called when the dialog window for coin1 exits
  const onToken1Selected = (address, symbol) => {
    // Close the dialog window
    setDialog1Open(false);

    // If the user inputs the same token, we want to switch the data in the fields
    if (address === coin2.address) {
      switchFields();
    }
    // We only update the values if the user provides a token
    else if (address && symbol) {
      // Getting some token data is async, so we need to wait for the data to return, hence the promise
      getBalanceAndSymbol(
        network.account,
        address,
        network.wethAddress,
        network.coins
      ).then((data) => {
        setCoin1({
          address: address,
          symbol: symbol,
          balance: data.balance,
          decimal: data.decimal,
        });
        changeQueryParam(
          "liquidity",
          symbol,
          coin2.symbol || searchParams.get("tokenOut")
        );
      });
    }
  };

  // Called when the dialog window for coin2 exits
  const onToken2Selected = (address, symbol) => {
    // Close the dialog window
    setDialog2Open(false);

    // If the user inputs the same token, we want to switch the data in the fields
    if (address === coin1.address) {
      switchFields();
    }
    // We only update the values if the user provides a token
    else if ((address, symbol)) {
      // Getting some token data is async, so we need to wait for the data to return, hence the promise
      getBalanceAndSymbol(
        network.account,
        address,
        network.wethAddress,
        network.coins
      ).then((data) => {
        setCoin2({
          address: address,
          symbol: symbol,
          balance: data.balance,
          decimal: data.decimal,
        });
        changeQueryParam(
          "liquidity",
          coin1.symbol || searchParams.get("tokenIn"),
          symbol
        );
      });
    }
  };

  // This method calls when user have query param for select coin
  // method search coin with api
  // and fill the set coin (coin1 or coin2)
  const search_coin = async (coinNumber, searchObject) => {
    let params = new URLSearchParams();
    for (const [key, value] of Object.entries(searchObject)) {
      params.set(key, value);
    }

    await searchCoin(params).then((res) => {
      if (res) {
        if (coinNumber === "tokenIn") {
          onToken1Selected("0x6485E05a01a7E019d3500Bb2321ce7eb10e603f9", [
            "USDT",
            "ECG",
          ]);
        } else {
          onToken2Selected(
            "0x90b07173E43B4ec3435dee3d8F969ac066342AB8",
            "Matic"
          );
        }
      }
    });
  };

  // This hook is called when either of the state variables `coin1.address` or `coin2.address` change.
  // This means that when the user selects a different coin to convert between, or the coins are swapped,
  // the new reserves will be calculated.
  useEffect(() => {
    console.log(
      "Trying to get reserves between:\n" + coin1.address + "\n" + coin2.address
    );

    if (coin1.address && coin2.address && network.account) {
      getReserves(
        coin1.address,
        coin2.address,
        network.factory,
        network.account
      ).then((data) => {
        setReserves([data[0], data[1]]);
        setLiquidityTokens(data[2]);
      });
    }
  }, [
    coin1.address,
    coin2.address,
    network.account,
    network.factory,
    network.signer,
  ]);

  // This hook is called when either of the state variables `field1Value`, `field2Value`, `coin1.address` or `coin2.address` change.
  // It will give a preview of the liquidity deployment.
  useEffect(() => {
    if (coin1.address && coin2.address) {
      console.log("Trying to preview the liquidity deployment");
      getAmountOut(coin1.address, coin2.address, "1", network.router).then(
        (res) => {
          if (res) {
            quoteAddLiquidity(
              coin1.address,
              coin2.address,
              "1",
              "10",
              network.factory
            ).then((data) => {
              console.log(data);
              console.log("TokenA in: ", data[0]);
              console.log("TokenB in: ", data[1]);
              console.log("Liquidity out: ", data[2]);
              const amount1 = parseFloat(data[0]).toPrecision(8);
              const amount2 = parseFloat(data[1]).toPrecision(8);

              setAmountsRate([amount1, amount2]);

              // setField1Value(parseFloat(data[0]).toPrecision(8));
              // setField2Value(parseFloat(data[1]).toPrecision(8));
              // setLiquidityOut([data[0], data[1], data[2]]);
            });
          }
        }
      );
    }
  }, [coin1.address, coin2.address, network.factory, network.signer]);

  useEffect(() => {
    if (field1Value) {
      handleChange.field1({ target: { value: field1Value } });
    }
  }, [amountsRate]);

  // This hook creates a timeout that will run every ~10 seconds, it's role is to check if the user's balance has
  // updated has changed. This allows them to see when a transaction completes by looking at the balance output.
  useEffect(() => {
    const coinTimeout = setTimeout(() => {
      console.log("Checking balances & Getting reserves...");

      if (coin1.address && coin2.address && network.account) {
        getReserves(
          coin1.address,
          coin2.address,
          network.factory,
          network.account
        ).then((data) => {
          setReserves([data[0], data[1]]);
          setLiquidityTokens(data[2]);
        });
      }

      if (coin1.address && network.account && !wrongNetworkOpen) {
        getBalanceAndSymbol(
          network.account,
          coin1.address,
          network.wethAddress,
          network.coins
        ).then((data) => {
          setCoin1({
            ...coin1,
            balance: data.balance,
          });
        });
      }
      if (coin2.address && network.account && !wrongNetworkOpen) {
        getBalanceAndSymbol(
          network.account,
          coin2.address,
          network.wethAddress,
          network.coins
        ).then((data) => {
          setCoin2({
            ...coin2,
            balance: data.balance,
          });
        });
      }
    }, 10000);

    return () => clearTimeout(coinTimeout);
  });

  // useEffect(() => {
  //   const queryTimeout = setTimeout(() => {
  //     if (network.chainID) {
  //       const tokenIn = searchParams.get("tokenIn");
  //       const tokenOut = searchParams.get("tokenOut");

  //       if (tokenIn) {
  //         search_coin("tokenIn", {
  //           page: 1,
  //           coin: tokenIn,
  //           chainId: network.chainID,
  //         });
  //       }

  //       if (tokenOut) {
  //         search_coin("tokenOut", {
  //           page: 1,
  //           coin: tokenOut,
  //           chainId: network.chainID,
  //         });
  //       }
  //     }
  //   }, 1000);
  //   return () => clearTimeout(queryTimeout);
  // }, [network]);

  return (
    <div>
      {" "}
      {/* Liquidity deployer */}{" "}
      <Typography variant="h5" className={classes.title}>
        {" "}
      </Typography>{" "}
      <CoinDialog
        open={dialog1Open}
        onClose={onToken1Selected}
        coins={network.coins}
        account={network.account}
        weth={network.wethAddress}
        chainId={network.chainID}
      />{" "}
      <CoinDialog
        open={dialog2Open}
        onClose={onToken2Selected}
        coins={network.coins}
        account={network.account}
        weth={network.wethAddress}
        chainId={network.chainID}
      />{" "}
      <WrongNetwork open={wrongNetworkOpen} />{" "}
      <Grid container direction="column" alignItems="center" spacing={2}>
        <Grid item xs={12} className={classes.fullWidth}>
          <CoinField
            activeField={true}
            value={field1Value}
            onClick={() => setDialog1Open(true)}
            onChange={handleChange.field1}
            symbol={coin1.symbol}
            balance={coin1.balance}
            decimal={coin1.decimal}
          />{" "}
        </Grid>{" "}
        <Grid item xs={12} className={classes.fullWidth}>
          <CoinField
            activeField={true}
            value={field2Value}
            onClick={() => setDialog2Open(true)}
            onChange={handleChange.field2}
            symbol={coin2.symbol}
            balance={coin2.balance}
            decimal={coin2.decimal}
          />{" "}
        </Grid>{" "}
      </Grid>{" "}
      {coin1.address && coin2.address && (
        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={4}
          className={classes.balance}
        >
          <hr className={classes.hr} />{" "}
          <Grid
            container
            item
            className={classes.values}
            direction="column"
            alignItems="center"
            md={6}
            xs={12}
          >
            {" "}
            {/* Reserves Display */}{" "}
            <Typography variant="h6"> Reserves </Typography>{" "}
            <Grid container direction="row" justifyContent="space-between">
              <Grid item xs={6}>
                <Typography variant="body1" className={classes.balance}>
                  {" "}
                  {formatReserve(reserves[0], coin1.symbol)}{" "}
                </Typography>{" "}
              </Grid>{" "}
              <Grid item xs={6}>
                <Typography variant="body1" className={classes.balance}>
                  {" "}
                  {formatReserve(reserves[1], coin2.symbol)}{" "}
                </Typography>{" "}
              </Grid>{" "}
            </Grid>{" "}
            <hr className={classes.hr} />{" "}
            <Typography variant="h6"> Your Liquidity Pool Tokens </Typography>{" "}
            <Grid container direction="row" justifyContent="center">
              <Grid item xs={12}>
                <Typography variant="body1" className={classes.balance}>
                  {" "}
                  {formatReserve(liquidityTokens, [
                    coin1.symbol,
                    coin2.symbol,
                  ])}{" "}
                </Typography>{" "}
              </Grid>{" "}
            </Grid>{" "}
          </Grid>{" "}
          <Grid md={6} container item>
            <Paper className={classes.paperContainer}>
              {" "}
              {/*Blue  Display to show the quote */}{" "}
              <Grid
                container
                item
                direction="column"
                alignItems="center"
                className={classes.fullWidth}
                xs={12}
              >
                {" "}
                {/* Tokens in */}{" "}
                <Typography variant="h6"> Tokens in </Typography>{" "}
                <Grid container direction="row" justifyContent="space-between">
                  <Grid item xs={6}>
                    <Typography variant="body1" className={classes.balance}>
                      {" "}
                      {formatBalance(liquidityOut[0], coin1.symbol)}{" "}
                    </Typography>{" "}
                  </Grid>{" "}
                  <Grid item xs={6}>
                    <Typography variant="body1" className={classes.balance}>
                      {" "}
                      {formatBalance(liquidityOut[1], coin2.symbol)}{" "}
                    </Typography>{" "}
                  </Grid>{" "}
                </Grid>{" "}
                <hr className={classes.hr} />{" "}
                <Typography variant="h6">
                  {" "}
                  Liquidity Pool Tokens Out{" "}
                </Typography>{" "}
                <Grid container direction="row" justifyContent="center">
                  <Grid item xs={6}>
                    <Typography variant="body1" className={classes.balance}>
                      {" "}
                      {formatReserve(liquidityOut[2], [
                        coin1.symbol,
                        coin2.symbol,
                      ])}{" "}
                    </Typography>{" "}
                  </Grid>{" "}
                </Grid>{" "}
              </Grid>{" "}
            </Paper>{" "}
          </Grid>{" "}
          <hr className={classes.hr} />{" "}
        </Grid>
      )}{" "}
      <Grid
        container
        direction="column"
        alignItems="center"
        spacing={2}
        className={coin1.address && coin2.address ? "mt-0" : "mt-2"}
      >
        <LoadingButton
          loading={loading}
          valid={isButtonEnabled()}
          success={false}
          fail={false}
          onClick={deploy}
        >
          <AccountBalanceIcon className={classes.buttonIcon} />{" "}
          {loading ? "processing" : "Deploy"}{" "}
        </LoadingButton>{" "}
      </Grid>{" "}
    </div>
  );
}

export default LiquidityDeployer;
